import { useCallback, useEffect, useRef, useState } from 'react'

import { useDispatch } from 'react-redux'

import type { RecipeUUID, RecipeURN } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'

type BasketRecipe = {
  urn?: RecipeURN
  numPortions: number
  recipe: { id: RecipeUUID }
}

type RemoveRecipe = (id: RecipeUUID, view: string) => void
type UpdateRecipePortions = (id: RecipeUUID, numPortions: number) => void

type PendingOperation =
  | {
      errorAtStart: unknown
      hasStarted: boolean
      numPortions: number
      recipeId: RecipeUUID
      recipeUrn?: RecipeURN
      type: 'remove'
    }
  | {
      errorAtStart: unknown
      hasStarted: boolean
      previousNumPortions: number
      recipeId: RecipeUUID
      type: 'update-portions'
    }

export type BasketRecipeError = {
  message: string
  recipeId?: RecipeUUID
  title: string
}

type UseBasketRecipeErrorHandlingInput = {
  basketError?: unknown
  basketRecipes: BasketRecipe[]
  isBasketValidating?: boolean
  removeRecipe: RemoveRecipe
  updateRecipePortions: UpdateRecipePortions
}

const REMOVE_RECIPE_ERROR: BasketRecipeError = {
  title: "Couldn't remove recipe",
  message: "It's back in your basket. Please try again.",
}

const UPDATE_PORTIONS_ERROR: BasketRecipeError = {
  title: "Couldn't update portions",
  message: "It's back to your previous portion size. Please try again.",
}

export const useBasketRecipeErrorHandling = ({
  basketError,
  basketRecipes,
  isBasketValidating = false,
  removeRecipe,
  updateRecipePortions,
}: UseBasketRecipeErrorHandlingInput) => {
  const dispatch = useDispatch()
  const pendingOperation = useRef<PendingOperation | null>(null)
  const hasRecoveryValidationStarted = useRef(false)
  const [recipeError, setRecipeError] = useState<BasketRecipeError | null>(null)

  const removeRecipeWithErrorHandling = useCallback<RemoveRecipe>(
    (recipeId, view) => {
      const recipe = basketRecipes.find(({ recipe: item }) => item.id === recipeId)

      if (recipe) {
        pendingOperation.current = {
          errorAtStart: basketError,
          hasStarted: false,
          numPortions: recipe.numPortions,
          recipeId,
          recipeUrn: recipe.urn,
          type: 'remove',
        }
        setRecipeError(null)
      }

      removeRecipe(recipeId, view)
    },
    [basketError, basketRecipes, removeRecipe],
  )

  const updateRecipePortionsWithErrorHandling = useCallback<UpdateRecipePortions>(
    (recipeId, numPortions) => {
      const recipe = basketRecipes.find(({ recipe: item }) => item.id === recipeId)

      if (recipe) {
        pendingOperation.current = {
          errorAtStart: basketError,
          hasStarted: false,
          previousNumPortions: recipe.numPortions,
          recipeId,
          type: 'update-portions',
        }
        setRecipeError(null)
      }

      updateRecipePortions(recipeId, numPortions)
    },
    [basketError, basketRecipes, updateRecipePortions],
  )

  useEffect(() => {
    const operation = pendingOperation.current

    if (!operation) return

    if (isBasketValidating) {
      operation.hasStarted = true
    }

    if (basketError && basketError !== operation.errorAtStart) {
      if (operation.type === 'remove') {
        dispatch({
          type: actionTypes.BASKET_RECIPE_ADD,
          recipeId: operation.recipeId,
          ...(operation.recipeUrn !== undefined && { recipeUrn: operation.recipeUrn }),
          numPortions: operation.numPortions,
        })
        setRecipeError({ ...REMOVE_RECIPE_ERROR, recipeId: operation.recipeId })
      } else {
        updateRecipePortions(operation.recipeId, operation.previousNumPortions)
        setRecipeError(UPDATE_PORTIONS_ERROR)
      }

      pendingOperation.current = null

      return
    }

    if (operation.hasStarted && !isBasketValidating) {
      pendingOperation.current = null
    }
  }, [basketError, dispatch, isBasketValidating, updateRecipePortions])

  useEffect(() => {
    if (!recipeError) {
      hasRecoveryValidationStarted.current = false

      return
    }

    if (isBasketValidating) {
      hasRecoveryValidationStarted.current = true

      return
    }

    if (hasRecoveryValidationStarted.current && !basketError) {
      hasRecoveryValidationStarted.current = false
      setRecipeError(null)
    }
  }, [basketError, isBasketValidating, recipeError])

  return {
    dismissRecipeError: () => setRecipeError(null),
    recipeError,
    removeRecipe: removeRecipeWithErrorHandling,
    updateRecipePortions: updateRecipePortionsWithErrorHandling,
  }
}
